import type {
  IReservationArrivalTime,
  IReservationDurationRule,
} from '@/interfaces/IReservationSettings';
import { timeToMinutes } from './reservation-time';

/**
 * Wall-clock port of the API arrival rule (reservations-availability.util.ts):
 * a shift offers one arrival every `slotIntervalMin` (its own) from its start,
 * or its fixed arrival times, and a time is offered to a party only when that
 * party's table time ends by the shift end. Pinned to the API by the shared
 * vector file in __tests__/arrival-times.vectors.json. Timezone-free, so a DST
 * night may differ by one chip from the engine; every other night is identical.
 */
export const RESERVATION_MIN_DURATION_MIN = 30;
export const RESERVATION_MAX_DURATION_MIN = 360;
export const RESERVATION_MAX_COVERS = 32767;

export type TArrivalTimeIssue =
  | 'invalid'
  | 'outside'
  | 'tail'
  | 'duplicate'
  | 'cap';

/** The branch-wide table times every shift is previewed against. */
export interface IArrivalPreviewSettings {
  defaultDurationMin: number;
  durationRules: readonly IReservationDurationRule[];
}

/** Only the table times of the branch settings reach the arrival previews. */
export function arrivalPreviewSettings(
  settings: IArrivalPreviewSettings
): IArrivalPreviewSettings {
  return {
    defaultDurationMin: settings.defaultDurationMin,
    durationRules: settings.durationRules,
  };
}

export interface IArrivalPreviewShift {
  from: string;
  to: string;
  arrivalTimes: readonly IReservationArrivalTime[] | null;
  /** Minutes between grid arrivals; unused with fixed times. */
  slotIntervalMin: number;
}

/** Party sizes from `from` up to `to` (null = no upper bound). */
export interface IPartyRange {
  from: number;
  to: number | null;
}

export interface IArrivalPreviewTime {
  time: string;
  offsetMin: number;
  /** The arrival happens after midnight, on the next calendar date. */
  nextDay: boolean;
  maxCovers: number | null;
  /** Party sizes offered this arrival with the current table times; empty when nobody is. */
  served: readonly IPartyRange[];
  /** Every party size is offered this arrival. */
  fitsAll: boolean;
  /** Set when only parties of 1 to this size get it (shorter table time); null otherwise. */
  fitsUpToParty: number | null;
}

const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export function isValidTime(time: string): boolean {
  return TIME_PATTERN.test(time);
}

export function minutesToTime(minutes: number): string {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(
    wrapped % 60
  ).padStart(2, '0')}`;
}

/** Minutes after the window start, wrapping at midnight (same rule as the API). */
export function arrivalOffsetMin(from: string, time: string): number {
  return (timeToMinutes(time) - timeToMinutes(from) + 1440) % 1440;
}

/** Length of a [from, to) window; 0 when the hours are equal (invalid). */
export function windowLengthMin(from: string, to: string): number {
  return arrivalOffsetMin(from, to);
}

export function clampDuration(minutes: number): number {
  return Math.min(
    RESERVATION_MAX_DURATION_MIN,
    Math.max(RESERVATION_MIN_DURATION_MIN, minutes)
  );
}

/** First rule the party fits in, else the default; mirrors the API `durationFor`. */
export function durationForParty(
  settings: Pick<
    IArrivalPreviewSettings,
    'defaultDurationMin' | 'durationRules'
  >,
  party: number
): number {
  const rule = settings.durationRules.find((item) => party <= item.maxParty);
  return clampDuration(rule?.minutes ?? settings.defaultDurationMin);
}

/** Both hours are HH:mm and differ, so the window has a length. */
export function hoursValid(from: string, to: string): boolean {
  return isValidTime(from) && isValidTime(to) && from !== to;
}

/**
 * Party brackets with their table time, in the order the API resolves them:
 * each rule serves the parties up to its `maxParty` that no earlier rule took,
 * and the default time serves everyone above the last rule.
 */
function partyBrackets(
  settings: Pick<
    IArrivalPreviewSettings,
    'defaultDurationMin' | 'durationRules'
  >
): { range: IPartyRange; minutes: number }[] {
  const brackets: { range: IPartyRange; minutes: number }[] = [];
  let covered = 0;
  for (const rule of settings.durationRules) {
    if (rule.maxParty <= covered) continue;
    brackets.push({
      range: { from: covered + 1, to: rule.maxParty },
      minutes: clampDuration(rule.minutes),
    });
    covered = rule.maxParty;
  }
  brackets.push({
    range: { from: covered + 1, to: null },
    minutes: clampDuration(settings.defaultDurationMin),
  });
  return brackets;
}

/** Joins adjacent ranges, e.g. 1-2 and 3-4 into 1-4. */
function mergeRanges(ranges: readonly IPartyRange[]): IPartyRange[] {
  const merged: IPartyRange[] = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && last.to != null && last.to + 1 === range.from)
      last.to = range.to;
    else merged.push({ ...range });
  }
  return merged;
}

/** Whether a party of this size is offered the previewed arrival. */
export function previewServesParty(
  time: IArrivalPreviewTime,
  party: number
): boolean {
  return time.served.some(
    (range) => party >= range.from && (range.to == null || party <= range.to)
  );
}

export function sortArrivalTimes<T extends { time: string }>(
  from: string,
  list: readonly T[]
): T[] {
  if (!isValidTime(from)) return [...list];
  return [...list].sort((a, b) => {
    const valid = isValidTime(a.time) && isValidTime(b.time);
    return valid
      ? arrivalOffsetMin(from, a.time) - arrivalOffsetMin(from, b.time)
      : 0;
  });
}

/** One issue per row (null = fine), mirroring the API normalisation rules plus the cap range. */
export function arrivalTimeIssues(
  from: string,
  to: string,
  list: readonly IReservationArrivalTime[]
): (TArrivalTimeIssue | null)[] {
  const valid = hoursValid(from, to);
  const length = valid ? windowLengthMin(from, to) : 0;
  const seen = new Set<number>();
  return list.map((row) => {
    if (!isValidTime(row.time)) return 'invalid';
    if (
      row.maxCovers != null &&
      (!Number.isInteger(row.maxCovers) ||
        row.maxCovers < 1 ||
        row.maxCovers > RESERVATION_MAX_COVERS)
    )
      return 'cap';
    if (!valid) return null;
    const offset = arrivalOffsetMin(from, row.time);
    if (offset >= length) return 'outside';
    if (offset + RESERVATION_MIN_DURATION_MIN > length) return 'tail';
    if (seen.has(offset)) return 'duplicate';
    seen.add(offset);
    return null;
  });
}

export function arrivalTimesValid(
  from: string,
  to: string,
  list: readonly IReservationArrivalTime[]
): boolean {
  return (
    list.length > 0 &&
    arrivalTimeIssues(from, to, list).every((issue) => issue === null)
  );
}

/** Next arrival to propose when adding a row: the last one plus the interval, or the window start. */
export function nextArrivalTime(
  from: string,
  list: readonly IReservationArrivalTime[],
  intervalMin: number
): string {
  const valid = list.filter((row) => isValidTime(row.time));
  if (!valid.length || !isValidTime(from))
    return isValidTime(from) ? from : '20:00';
  const last = sortArrivalTimes(from, valid)[valid.length - 1];
  return minutesToTime(timeToMinutes(last.time) + intervalMin);
}

/** The arrival times a guest sees for a shift, with the party bracket each one serves. */
export function previewArrivalTimes(
  shift: IArrivalPreviewShift,
  settings: IArrivalPreviewSettings
): IArrivalPreviewTime[] {
  if (!hoursValid(shift.from, shift.to)) return [];
  const length = windowLengthMin(shift.from, shift.to);
  const fromMin = timeToMinutes(shift.from);
  const brackets = partyBrackets(settings);
  const shortest = Math.min(...brackets.map((bracket) => bracket.minutes));
  const describe = (
    offset: number,
    maxCovers: number | null
  ): IArrivalPreviewTime => {
    const served = mergeRanges(
      brackets
        .filter((bracket) => offset + bracket.minutes <= length)
        .map((bracket) => bracket.range)
    );
    const prefix =
      served.length === 1 && served[0].from === 1 ? served[0] : null;
    return {
      time: minutesToTime(fromMin + offset),
      offsetMin: offset,
      nextDay: fromMin + offset >= 1440,
      maxCovers,
      served,
      fitsAll: prefix?.to === null,
      fitsUpToParty: prefix?.to ?? null,
    };
  };
  if (shift.arrivalTimes) {
    // Equal offsets collapse to the first row, as the API engine does.
    const rows = sortArrivalTimes(
      shift.from,
      shift.arrivalTimes.filter(
        (row) =>
          isValidTime(row.time) &&
          arrivalOffsetMin(shift.from, row.time) < length
      )
    );
    return rows
      .map((row) => ({ offset: arrivalOffsetMin(shift.from, row.time), row }))
      .filter(
        (item, index, list) =>
          index === 0 || list[index - 1].offset !== item.offset
      )
      .map((item) => describe(item.offset, item.row.maxCovers));
  }
  const times: IArrivalPreviewTime[] = [];
  const interval = Math.max(1, shift.slotIntervalMin);
  for (let offset = 0; offset + shortest <= length; offset += interval) {
    times.push(describe(offset, null));
  }
  return times;
}
